import {
    booleanCodec,
    dateRangeCodec,
    encodedArrayCodec,
    withModeCodec,
} from "../table/codecs/filterValueCodecs";

import { classificationIdCodec } from "./classificationIdCodec";
import { controlActivitiesDateFilterCodec } from "./controlActivitiesDateFilterCodec";
import { timeToDueDateFilterCodec } from "./timeToDueDateFilterCodec";

export const CONTROL_ACTIVITIES_LIST_COLUMN_IDS = {
    CONTROL_PROGRAM_ID: "controlProgramId",
    CONTROL_PROGRAM_STATUS: "controlProgramStatus",
    CONTROL_ID: "controlId",
    DESCRIPTION: "description",
    PERIOD: "period",
    TAGS: "tags",
    CREATED: "created",
    ENTITY: "entity",
    COMPLETION_DUE_DATE: "completionDueDate",
    FIRST_REVIEWER_DUE_DATE: "firstReviewerDueDate",
    SECOND_REVIEWER_DUE_DATE: "secondReviewerDueDate",
    DUE_DATE: "dueDate",
    TIME_TO_DUE_DATE: "timeToDueDate",
    COMPLETED_DATE: "completedDate",
    FIRST_REVIEWED_DATE: "firstReviewedDate",
    SECOND_REVIEWED_DATE: "secondReviewedDate",
    COMPLETED_AND_REVIEWED_DATE: "completedAndReviewedDate",
    RESPONSIBLE: "responsible",
    CLAIMANT: "claimant",
    PERFORMER: "performer",
    REVIEWED_DATE: "reviewedDate",
    REVIEWER: "reviewer",
    REVIEW_PERFORMER: "reviewPerformer",
    SECOND_REVIEWER: "secondReviewer",
    SECOND_REVIEW_PERFORMER: "secondReviewPerformer",
    RESPONSIBLE_OR_REVIEWER: "responsibleOrReviewer",
    WORKFLOW_STATUS: "workflowStatus",
    HEALTH: "health",
    FRICTION_TYPE: "frictionType",
    CLASSIFICATION_CLASS: "classificationClass",
    ARCHIVED: "archived",
} as const;

/**
 * Opting a column in here requires its accessor to return display text
 * (a string or string array) for contains/notContains to match against.
 */
const CONTAINS_FILTER_COLUMN_IDS = new Set<string>([
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_ID,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_ID,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TAGS,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ENTITY,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CLAIMANT,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.PERFORMER,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEWER,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEW_PERFORMER,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEW_PERFORMER,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE_OR_REVIEWER,
]);

export function supportsContainsFilter(columnId: string): boolean {
    return CONTAINS_FILTER_COLUMN_IDS.has(columnId);
}

/**
 * Free-text columns filtered only by contains/notContains: exact is/isNot
 * matching on a whole description makes no sense, so we don't offer it.
 */
const CONTAINS_ONLY_FILTER_COLUMN_IDS = new Set<string>([
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS.DESCRIPTION,
]);

export function supportsOnlyContainsFilter(columnId: string): boolean {
    return CONTAINS_ONLY_FILTER_COLUMN_IDS.has(columnId);
}

export const CONTROL_ACTIVITIES_DATA_PARAM = {
    ACTIVE: "active",
    ARCHIVED: "archived",
    DATE_FILTER: "dateFilter",
    CLASSIFICATION_ID: "classificationId",
} as const;

export type ControlActivitiesDateFilterMode = "dueDate" | "period"; // both mutually exclusive

export type PersistedControlActivitiesDateFilter = {
    mode: ControlActivitiesDateFilterMode;
    range: [string | null, string | null] | null;
} | null;

export const controlActivitiesListDataParamsCodecMap = {
    [CONTROL_ACTIVITIES_DATA_PARAM.ACTIVE]: booleanCodec,
    [CONTROL_ACTIVITIES_DATA_PARAM.ARCHIVED]: booleanCodec,
    [CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER]:
        controlActivitiesDateFilterCodec,
    [CONTROL_ACTIVITIES_DATA_PARAM.CLASSIFICATION_ID]: classificationIdCodec,
};

// DUE_DATE has no entry: the backend filters it through the date filter data
// param, not a column filter.
export const controlActivitiesListColumnFiltersCodecMap = {
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TIME_TO_DUE_DATE]: withModeCodec(
        timeToDueDateFilterCodec
    ),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_ID]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_STATUS]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_ID]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ENTITY]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.DESCRIPTION]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.PERIOD]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TAGS]: withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CREATED]: withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETION_DUE_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FIRST_REVIEWER_DUE_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER_DUE_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETED_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FIRST_REVIEWED_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWED_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETED_AND_REVIEWED_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CLAIMANT]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.PERFORMER]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEWED_DATE]:
        withModeCodec(dateRangeCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEWER]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEW_PERFORMER]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEW_PERFORMER]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE_OR_REVIEWER]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.WORKFLOW_STATUS]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.HEALTH]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FRICTION_TYPE]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CLASSIFICATION_CLASS]:
        withModeCodec(encodedArrayCodec),
    [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ARCHIVED]:
        withModeCodec(encodedArrayCodec),
};
